import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Tooltip,
} from "@mui/material";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";
import { useLanguage } from "../../Language";
import { axios } from "../../AxiosInstanceManager";
import { AxiosError } from "axios";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { LogoUploadDialog } from "./LogoUploadDialog";
import { useDialogRegistry } from "@tlc/ui/dialog/DialogRegistry";
import { useDialog } from "@tlc/ui/dialog/useDialog";

interface StoredImageView {
  id: number;
  category: string;
}

interface GetStoredImagesResponse {
  images: StoredImageView[];
}

interface RemoveStoredImageReturn {
  success: boolean;
}

export interface LogoSelectorProps<T extends FieldValues = FieldValues>
  extends Pick<UseControllerProps<T>, "name"> {
  bgcolor: string;
}

export const LogoSelector = <T extends FieldValues = FieldValues>({
  name,
  bgcolor,
  ...props
}: LogoSelectorProps<T>) => {
  const { control } = useFormContext<T>();
  const { field } = useController({ name, control });
  const { translate } = useLanguage();

  const [images, setImages] = useState<StoredImageView[]>();
  const [deleteImageId, setDeleteImageId] = useState<number>();
  const [deleteImageError, setDeleteImageError] = useState<string>();
  const [deleting, setDeleting] = useState<boolean>();
  const {
    open: logoUploadOpen,
    closeDialog: closeLogoUpload,
    openDialog: openLogoUpload,
  } = useDialog();

  const { registerDialog } = useDialogRegistry();
  useEffect(() => {
    return registerDialog(() => {
      setDeleteImageId(null);
      setDeleteImageError(null);
    });
  }, [registerDialog, setDeleteImageError, setDeleteImageId]);

  useEffect(() => {
    // TODO need a better loading state
    getLogos();
  }, []);

  const getLogos = async () => {
    return axios
      .get<GetStoredImagesResponse>(`/storedimage/byCategory/logos`)
      .then(({ data }) => {
        setImages(data.images);
      });
  };

  const removeStoredImage = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    imageId: number,
  ) => {
    event.preventDefault();
    setDeleteImageId(imageId);
  };

  const deleteImage = async () => {
    setDeleting(true);
    setDeleteImageError(null);

    try {
      const { data } = await axios.get<RemoveStoredImageReturn>(
        `storedimage/${deleteImageId}/remove`,
      );
      setDeleting(false);

      if (data.success) {
        SuccessMessageControl.show(translate("imageRemovedSuccess"));
        setImages(images.filter(({ id }) => id !== deleteImageId)); // remove rendered image
        closeDeleteConfirmation();
      } else {
        setDeleteImageError(translate("imageRemovedError"));
      }
    } catch (e) {
      setDeleteImageError((e as AxiosError<unknown>).message);
      setDeleting(false);
      console.error(e);
    }
  };

  const closeDeleteConfirmation = () => {
    if (deleting) {
      return;
    }
    setDeleteImageId(null);
    setDeleteImageError(null);
  };

  const onClickLogo = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    imageId: number,
  ) => {
    // just in case the user clicked on the remove action
    if (event.defaultPrevented === true) {
      return;
    }
    event.preventDefault();
    if (field.value === imageId) {
      field.onChange(null);
    } else {
      field.onChange(imageId);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          p: 2,
        }}
      >
        {(images || []).map((image) => (
          <Card key={image.id} sx={{ maxWidth: 345, minWidth: "120px" }}>
            <CardContent
              sx={{
                bgcolor: bgcolor,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Avatar
                variant="square"
                sx={{
                  height: 48,
                  maxWidth: "100%",
                  width: "auto",
                  "& > img": {
                    height: "100%",
                    maxWidth: "100%",
                    width: "auto",
                    objectFit: "contain",
                    //   objectPosition: 'top',
                  },
                }}
                alt={`${image.category} ${image.id}`}
                src={`/storedimage/${image.id}?h=80&fit=crop&auto=format`}
              />
            </CardContent>
            <CardActions
              sx={{
                justifyContent: "space-between",
              }}
            >
              {/* TODO language properties */}
              <Tooltip
                title={
                  image.id === field.value
                    ? translate("deSelectLogoToken")
                    : translate("selectLogoToken")
                }
              >
                <IconButton
                  onClick={(event) => onClickLogo(event, image.id)}
                  aria-label={
                    image.id === field.value
                      ? translate("deSelectLogoToken")
                      : translate("selectLogoToken")
                  }
                >
                  <>
                    {image.id === field.value && (
                      <CheckCircleOutlineIcon color="primary" />
                    )}
                    {image.id !== field.value && (
                      <RadioButtonUncheckedIcon color="primary" />
                    )}
                  </>
                </IconButton>
              </Tooltip>
              <Tooltip title={translate("removeLogo")}>
                <span>
                  <IconButton
                    disabled={field.value === image.id}
                    color="error"
                    onClick={(event) => removeStoredImage(event, image.id)}
                    aria-label={translate("removeLogo")}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </CardActions>
          </Card>
        ))}
        <Card sx={{ maxWidth: 345, minWidth: "120px", height: "144px" }}>
          <Tooltip title={translate("uploadInstructions")}>
            <CardActionArea sx={{ height: "100%" }} onClick={openLogoUpload}>
              <CardContent
                sx={{
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <AddCircleOutlineIcon color="primary" fontSize={"large"} />
              </CardContent>
            </CardActionArea>
          </Tooltip>
        </Card>
      </Box>
      <Dialog open={!!deleteImageId} onClose={closeDeleteConfirmation}>
        <DialogContent>
          {!deleteImageError && (
            <DialogContentText>
              {translate("areYouSureYouWantToRemoveThisImage")}
            </DialogContentText>
          )}
          {deleteImageError && (
            <Alert severity="error">{deleteImageError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={closeDeleteConfirmation}>
            {translate("cancel")}
          </Button>
          <Button
            disabled={!!deleteImageError || deleting}
            color="error"
            variant="outlined"
            startIcon={
              deleting ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <DeleteIcon />
              )
            }
            onClick={deleteImage}
            autoFocus
          >
            {translate("delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <LogoUploadDialog
        open={logoUploadOpen}
        onClose={closeLogoUpload}
        onUpload={async () => {
          await getLogos();
          closeLogoUpload();
        }}
      ></LogoUploadDialog>
    </>
  );
};
